import IParser from "@/types/IParser"
import ParseError from "@/types/ParseError"
import {
  ITradesElecData,
  ITradesElecHistoryData,
  ITradesElecNominationsData,
} from "./elecData.types"
import {
  TradesElecData,
  TradesElecHistoryData,
  TradesElecNominationsData,
} from "./elecData"
import {
  GetElecNominationsQuery,
  GetElecTradesHistoryQuery,
  GetElecTradesQuery,
} from "@/api/hasura/generated/graphql"
import {
  dayjsOrNull,
  numberOfDaysInInterval,
  numberOfWorkingDaysInInterval,
} from "@/helpers/dateHelper"
import { tradeProducts } from "@/constants/tradeOptions"
import dayjs from "dayjs"

export type ITradesElecDataRaw = GetElecTradesQuery["ElecTrades"][0]
export type ITradesElecHistoryDataRaw =
  GetElecTradesHistoryQuery["ElecTradesHistory"][0]
export type ITradesElecNominationsDataRaw =
  GetElecNominationsQuery["ElecNominations"][0]

const computeTotalVolume = (
  volume?: number | null,
  product?: string | null,
  dateFrom?: Date | null,
  dateTo?: Date | null,
) => {
  if (volume && product) {
    const daysForProduct = tradeProducts[product]?.workingDaysOnly
      ? numberOfWorkingDaysInInterval(dayjs(dateFrom), dayjs(dateTo))
      : numberOfDaysInInterval(dayjs(dateFrom), dayjs(dateTo))

    return volume * daysForProduct * tradeProducts[product]?.hours
  } else return 0
}

export const TradesElecDataParser: IParser<
  ITradesElecData,
  ITradesElecDataRaw
> = {
  toDomain(data: ITradesElecDataRaw): ITradesElecData {
    try {
      return new TradesElecData(
        data.id,
        dayjsOrNull(data.Date),
        data.Product ?? null,
        data.Period ?? null,
        data.Price ?? null,
        data.Volume ?? null,
        computeTotalVolume(
          data.Volume,
          data.Product,
          data.Invoice_Date_From,
          data.Invoice_Date_To,
        ),
        dayjsOrNull(data.Invoice_Date_From),
        dayjsOrNull(data.Invoice_Date_To),
        data.Counterparty ?? null,
        data.Trader ?? null,
        data.Notes ?? null,
        dayjsOrNull(data.ConfirmReceivedDate),
        dayjsOrNull(data.ConfirmSignedDate),
        data.ConfirmSignedBy ?? null,
        data.PercentOfBaseload ?? null,
        data.Locked,
      )
    } catch (error) {
      throw new ParseError(error)
    }
  },
}

export const TradesElecHistoryDataParser: IParser<
  ITradesElecHistoryData,
  ITradesElecHistoryDataRaw
> = {
  toDomain(data: ITradesElecHistoryDataRaw): ITradesElecHistoryData {
    try {
      return new TradesElecHistoryData(
        data.id,
        data.elec_trade_id,
        dayjsOrNull(data.Date),
        data.Product ?? null,
        data.Period ?? null,
        data.Price ?? null,
        data.Volume ?? null,
        computeTotalVolume(
          data.Volume,
          data.Product,
          data.Invoice_Date_From,
          data.Invoice_Date_To,
        ),
        dayjsOrNull(data.Invoice_Date_From),
        dayjsOrNull(data.Invoice_Date_To),
        data.Counterparty ?? null,
        data.Trader ?? null,
        data.Notes ?? null,
        dayjsOrNull(data.ConfirmReceivedDate),
        dayjsOrNull(data.ConfirmSignedDate),
        data.ConfirmSignedBy ?? null,
        data.PercentOfBaseload ?? null,
      )
    } catch (error) {
      throw new ParseError(error)
    }
  },
}

export const TradesElecNominationsDataParser: IParser<
  ITradesElecNominationsData,
  ITradesElecNominationsDataRaw
> = {
  toDomain(data: ITradesElecNominationsDataRaw): ITradesElecNominationsData {
    try {
      return new TradesElecNominationsData(
        data.Hour_1 ?? null,
        data.Hour_2 ?? null,
        data.Hour_3 ?? null,
        data.Hour_4 ?? null,
        data.Hour_5 ?? null,
        data.Hour_6 ?? null,
        data.Hour_7 ?? null,
        data.Hour_8 ?? null,
        data.Hour_9 ?? null,
        data.Hour_10 ?? null,
        data.Hour_11 ?? null,
        data.Hour_12 ?? null,
        data.Hour_13 ?? null,
        data.Hour_14 ?? null,
        data.Hour_15 ?? null,
        data.Hour_16 ?? null,
        data.Hour_17 ?? null,
        data.Hour_18 ?? null,
        data.Hour_19 ?? null,
        data.Hour_20 ?? null,
        data.Hour_21 ?? null,
        data.Hour_22 ?? null,
        data.Hour_23 ?? null,
        data.Hour_24 ?? null,
        data.Hour_25 ?? null,
        dayjsOrNull(data.created_at),
        dayjsOrNull(data.updated_at),
        dayjsOrNull(data.date),
        data.trader ?? null,
      )
    } catch (error) {
      throw new ParseError(error)
    }
  },
}
